import { Controller } from "@hotwired/stimulus"

// Sheet selection + symbol-ingest launch. Checkboxes live on Scope; the form
// lives on Detect (and may be in a hidden stage). Cockpit / Scope CTAs call
// #submitIngest which requestSubmit()s the real form so Turbo + ensureSelection run.
export default class extends Controller {
  static targets = ["pageCheckbox", "selectedCount", "selectionSummary", "runButton", "selectionFields", "saveForm", "saveFields"]
  static values = { saveUrl: String }

  connect() {
    this.recount()
    this._formData = (event) => {
      const form = event.target
      if (!form.matches?.("form.cd-ingest-form")) return
      form.querySelectorAll('input[type="checkbox"][name^="ingest_options["]').forEach((checkbox) => {
        event.formData.set(checkbox.name, checkbox.checked ? "1" : "0")
      })
    }
    this.element.addEventListener("formdata", this._formData)
  }

  disconnect() {
    this.element.removeEventListener("formdata", this._formData)
    clearTimeout(this._selTimer)
  }

  pageCheckboxTargetConnected() {
    this.recount()
  }

  persistSelection() {
    this.pageCheckboxes().forEach((el) => {
      const tr = el.closest("tr")
      if (tr) tr.classList.toggle("is-unselected", !el.checked)
    })
    this.recount()
    clearTimeout(this._selTimer)
    this._selTimer = setTimeout(() => this.submitSave({ selectionOnly: true }), 250)
  }

  saveSheets(event) {
    event?.preventDefault?.()
    this.submitSave({ selectionOnly: false, thenStage: null })
  }

  saveThenContinue(event) {
    event?.preventDefault?.()
    const stage = event?.currentTarget?.dataset?.stage || "legends"
    this.submitSave({ selectionOnly: false, thenStage: stage })
  }

  submitSave({ selectionOnly = false, thenStage = null } = {}) {
    const form = this.hasSaveFormTarget ? this.saveFormTarget : null
    if (!form) return

    if (this.hasSaveFieldsTarget) {
      this.saveFieldsTarget.innerHTML = ""
      this.appendHidden(this.saveFieldsTarget, "selection_only", selectionOnly ? "1" : "0")
      this.pageCheckboxes()
        .filter((el) => el.checked)
        .forEach((el) => this.appendHidden(this.saveFieldsTarget, "selected_pages[]", el.value))
      // Always send selected_pages so unchecking every row persists as empty.
      if (this.pageCheckboxes().filter((el) => el.checked).length === 0) {
        this.appendHidden(this.saveFieldsTarget, "selected_pages[]", "")
      }
      if (!selectionOnly) {
        this.element.querySelectorAll("tr[id^='sheet_row_']").forEach((tr, index) => {
          const prefix = `sheets[${index}]`
          const id = (tr.id || "").replace("sheet_row_", "")
          const page = tr.dataset.sheetPage || ""
          this.appendHidden(this.saveFieldsTarget, `${prefix}[id]`, id)
          this.appendHidden(this.saveFieldsTarget, `${prefix}[page_number]`, page)
          this.appendField(this.saveFieldsTarget, tr, `${prefix}[sheet_number]`, "construction_drawing_sheet[sheet_number]")
          this.appendField(this.saveFieldsTarget, tr, `${prefix}[sheet_name]`, "construction_drawing_sheet[sheet_name]")
          this.appendField(this.saveFieldsTarget, tr, `${prefix}[focus_family]`, "construction_drawing_sheet[focus_family]")
          this.appendField(this.saveFieldsTarget, tr, `${prefix}[sheet_discipline]`, "construction_drawing_sheet[sheet_discipline]")
          this.appendField(this.saveFieldsTarget, tr, `${prefix}[role]`, "construction_drawing_sheet[role]")
        })
      }
    }

    const go = () => {
      if (!thenStage) return
      try {
        const shell = this.application.getControllerForElementAndIdentifier(this.element, "cd-shell")
        shell?.show?.(thenStage)
      } catch (_e) {
        /* ignore */
      }
    }

    if (thenStage) {
      const onEnd = (e) => {
        if (e.target !== form) return
        form.removeEventListener("turbo:submit-end", onEnd)
        if (e.detail?.success !== false) go()
      }
      form.addEventListener("turbo:submit-end", onEnd)
    }

    if (typeof form.requestSubmit === "function") {
      form.requestSubmit()
    } else {
      form.submit()
    }
  }

  appendHidden(root, name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value == null ? "" : String(value)
    root.appendChild(input)
  }

  appendField(root, tr, asName, fromName) {
    const el = tr.querySelector(`[name="${fromName}"]`)
    this.appendHidden(root, asName, el ? el.value : "")
  }

  recount() {
    const boxes = this.pageCheckboxes()
    // Count CHECKED regardless of disabled — during a run every checkbox is
    // locked, and "0 of 7 selected" over six checked boxes reads as broken.
    const checked = boxes.filter((el) => el.checked)
    const n = checked.length

    this.selectedCountTargets.forEach((el) => {
      el.textContent = String(n)
    })
    const plans = checked.filter((el) => el.closest("tr")?.dataset.sheetRole === "markup").length
    const references = checked.filter((el) => el.closest("tr")?.dataset.sheetRole === "reference").length
    const other = n - plans - references
    const scope = [`${plans} plan sheet${plans === 1 ? "" : "s"} selected`]
    if (references) scope.push(`${references} reference sheet${references === 1 ? "" : "s"}`)
    if (other) scope.push(`${other} other sheet${other === 1 ? "" : "s"}`)
    this.selectionSummaryTargets.forEach((el) => { el.textContent = scope.join(" · ") })

    this.runButtonTargets.forEach((btn) => {
      const busy = btn.dataset.ingestBusy === "true"
      // Sticky force_disabled only applies while busy OR when there is no selection.
      // After roles/selection recover, clear it so Run is clickable again.
      if (btn.dataset.forceDisabled === "true" && !busy && n > 0) {
        btn.dataset.forceDisabled = "false"
      }
      if (btn.dataset.forceDisabled === "true") {
        btn.disabled = true
        return
      }
      btn.disabled = busy || n === 0
      if (!busy && n === 0) {
        btn.title = "Select at least one sheet for HexaPDF native bind"
      } else if (!busy) {
        btn.title = `Run HexaPDF native bind on ${n} selected sheet${n === 1 ? "" : "s"} (ovals, polylines, polygons, linears)`
      }
    })
  }

  // Cockpit / Scope external CTAs — never fake hash links.
  submitIngest(event) {
    event.preventDefault()
    const btn = event.currentTarget
    if (btn?.disabled) return
    // force_disabled is a server-render hint only — recompute from selection
    // so a stale "true" after identify/task clear doesn't permanently kill Run.
    if (btn?.dataset?.forceDisabled === "true") {
      const n = this.pageCheckboxes().filter((el) => el.checked).length
      if (n === 0) {
        window.alert("Select at least one sheet (and set Markup plan roles) before HexaPDF native bind.")
        return
      }
      // Selection exists — allow submit; server re-validates readiness.
      btn.dataset.forceDisabled = "false"
      btn.disabled = false
    }

    const formId = btn.dataset.ingestFormId
    const form =
      (formId && document.getElementById(formId)) ||
      this.element.querySelector("form.cd-ingest-form") ||
      this.element.querySelector("form[id^='ingest_select_form_']")

    if (!form) {
      window.alert("Ingest form not ready. Open Detect and try again.")
      return
    }

    // Show Detect so progress rail is visible after enqueue.
    try {
      const shell = this.application.getControllerForElementAndIdentifier(this.element, "cd-shell")
      shell?.show?.("detect")
    } catch (_e) {
      /* ignore */
    }

    // requestSubmit fires the form's submit handlers (ensureSelection) and
    // Turbo intercepts them. Falls back to form.submit() only if needed.
    if (typeof form.requestSubmit === "function") {
      form.requestSubmit()
    } else {
      this.ensureSelection({ preventDefault() {}, target: form })
      form.submit()
    }
  }

  // The run form is fully decoupled from the table: on submit, inject the
  // checked pages as hidden fields into ITS OWN form (checkboxes are pure
  // UI state — no form= coupling, no nesting).
  //
  // Count CHECKED even when disabled: a stuck busy lock must not silently
  // drop selection (and the server re-validates). External form= submitters
  // (cockpit CTA) also rely on this path.
  ensureSelection(event) {
    const form = event.target?.closest?.("form") || event.target
    form?.querySelectorAll?.('input[type="checkbox"][name^="ingest_options["]').forEach((checkbox) => {
      const hidden = Array.from(form.querySelectorAll('input[type="hidden"]')).find((input) => input.name === checkbox.name)
      if (hidden) hidden.value = checkbox.checked ? "1" : "0"
    })
    const checked = this.pageCheckboxes().filter((el) => el.checked)
    if (checked.length === 0) {
      event.preventDefault()
      window.alert("Select at least one sheet for HexaPDF native bind.")
      return
    }
    if (!this.hasSelectionFieldsTarget) {
      // Still allow native submit if fields target is missing — server uses
      // inferred pages — but prefer injecting when present.
      return
    }

    this.selectionFieldsTarget.innerHTML = ""
    checked.forEach((el) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "selected_pages[]"
      input.value = el.value
      this.selectionFieldsTarget.appendChild(input)
    })
  }

  pageCheckboxes() {
    if (this.hasPageCheckboxTarget) return this.pageCheckboxTargets
    return [...this.element.querySelectorAll(".ingest-page-checkbox")]
  }
}
